import { useEffect, useState, useCallback } from 'react'

// 定义 Telegram WebApp 类型
interface TelegramWebApp {
  ready: () => void
  expand: () => void
  close: () => void
  enableClosingConfirmation: () => void
  disableClosingConfirmation: () => void
  platform: string
  version: string
  colorScheme: 'light' | 'dark'
  themeParams: {
    bg_color: string
    text_color: string
    hint_color: string
    link_color: string
    button_color: string
    button_text_color: string
    secondary_bg_color: string
  }
  initData: string
  initDataUnsafe: {
    query_id?: string
    user?: {
      id: number
      first_name: string
      last_name?: string
      username?: string
      language_code?: string
      is_premium?: boolean
      photo_url?: string
    }
    receiver?: object
    chat?: object
    start_param?: string
    can_send_after?: number
    auth_date: number
    hash: string
  }
  showPopup: (params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: 'default' | 'ok' | 'close' | 'cancel'; text: string }> }) => Promise<string>
  showAlert: (message: string) => Promise<void>
  showConfirm: (message: string) => Promise<boolean>
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
  MainButton: {
    text: string
    color: string
    textColor: string
    isVisible: boolean
    isActive: boolean
    isProgressVisible: boolean
    setText: (text: string) => void
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
    show: () => void
    hide: () => void
    enable: () => void
    disable: () => void
    showProgress: (leaveActive: boolean) => void
    hideProgress: () => void
    setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void
  }
  BackButton: {
    isVisible: boolean
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
    show: () => void
    hide: () => void
  }
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void
    selectionChanged: () => void
  }
}

// 定义 Hook 返回类型
interface UseTelegramReturn {
  webApp: TelegramWebApp | null
  user: TelegramWebApp['initDataUnsafe']['user'] | null
  platform: string
  ready: () => void
  expand: () => void
  close: () => void
  showAlert: (message: string) => Promise<void>
  showConfirm: (message: string) => Promise<boolean>
  showPopup: (params: Parameters<TelegramWebApp['showPopup']>[0]) => Promise<string>
  impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
  notificationOccurred: (type: 'error' | 'success' | 'warning') => void
  selectionChanged: () => void
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
}

export function useTelegram(): UseTelegramReturn {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [user, setUser] = useState<TelegramWebApp['initDataUnsafe']['user'] | null>(null)
  const [platform, setPlatform] = useState<string>('web')

  useEffect(() => {
    // 检查是否在 Telegram WebApp 环境中
    const tg = (window as any).Telegram?.WebApp
    
    if (tg) {
      setWebApp(tg as TelegramWebApp)
      setUser(tg.initDataUnsafe?.user || null)
      setPlatform(tg.platform || 'web')

      // 自动适配 Telegram 主题
      if (tg.colorScheme) {
        document.documentElement.setAttribute('data-theme', tg.colorScheme)
      }

      // 监听主题变化
      tg.onEvent('themeChanged', () => {
        document.documentElement.setAttribute('data-theme', tg.colorScheme)
      })
    } else {
      console.log('Not in Telegram WebApp environment, using mock data')
      // 开发环境使用模拟数据
      setPlatform('web')
    }

    return () => {
      if (tg) {
        tg.offEvent('themeChanged', () => {})
      }
    }
  }, [])

  const ready = useCallback(() => {
    if (webApp) {
      webApp.ready()
    }
  }, [webApp])

  const expand = useCallback(() => {
    if (webApp) {
      webApp.expand()
    }
  }, [webApp])

  const close = useCallback(() => {
    if (webApp) {
      webApp.close()
    }
  }, [webApp])

  const showAlert = useCallback(
    async (message: string): Promise<void> => {
      if (webApp) {
        return webApp.showAlert(message)
      }
      // 开发环境回退到原生 alert
      alert(message)
    },
    [webApp]
  )

  const showConfirm = useCallback(
    async (message: string): Promise<boolean> => {
      if (webApp) {
        return webApp.showConfirm(message)
      }
      // 开发环境回退到原生 confirm
      return confirm(message)
    },
    [webApp]
  )

  const showPopup = useCallback(
    async (params: Parameters<TelegramWebApp['showPopup']>[0]): Promise<string> => {
      if (webApp) {
        return webApp.showPopup(params)
      }
      // 开发环境回退到原生 confirm
      return confirm(params.message) ? 'ok' : 'cancel'
    },
    [webApp]
  )

  const impactOccurred = useCallback(
    (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => {
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.impactOccurred(style)
      }
    },
    [webApp]
  )

  const notificationOccurred = useCallback(
    (type: 'error' | 'success' | 'warning') => {
      if (webApp?.HapticFeedback) {
        webApp.HapticFeedback.notificationOccurred(type)
      }
    },
    [webApp]
  )

  const selectionChanged = useCallback(() => {
    if (webApp?.HapticFeedback) {
      webApp.HapticFeedback.selectionChanged()
    }
  }, [webApp])

  const setHeaderColor = useCallback(
    (color: string) => {
      if (webApp) {
        webApp.setHeaderColor(color)
      }
    },
    [webApp]
  )

  const setBackgroundColor = useCallback(
    (color: string) => {
      if (webApp) {
        webApp.setBackgroundColor(color)
      }
    },
    [webApp]
  )

  return {
    webApp,
    user,
    platform,
    ready,
    expand,
    close,
    showAlert,
    showConfirm,
    showPopup,
    impactOccurred,
    notificationOccurred,
    selectionChanged,
    setHeaderColor,
    setBackgroundColor,
  }
}

// 便捷的 Hook：仅获取用户信息的简化版本
export function useTelegramUser() {
  const { user } = useTelegram()
  return user
}

// 便捷的 Hook：获取 Telegram 主题
export function useTelegramTheme() {
  const { webApp } = useTelegram()
  
  return {
    colorScheme: webApp?.colorScheme || 'dark',
    themeParams: webApp?.themeParams,
    bgColor: webApp?.themeParams?.bg_color || '#0e1621',
    textColor: webApp?.themeParams?.text_color || '#ffffff',
    hintColor: webApp?.themeParams?.hint_color || '#768c9e',
    buttonColor: webApp?.themeParams?.button_color || '#0088cc',
  }
}
